import React, { useState } from "react";
import { toast } from "sonner";
import { FolderDown, Loader2 } from "lucide-react";

const limpiar = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();

const extDe = (f) => {
  const m = /\.([a-z0-9]{3,4})(\?|$)/i.exec(f.url || "");
  if (m) return m[1].toLowerCase();
  return f.tipo === "video" ? "mp4" : "jpg";
};

const urlDescarga = (url) =>
  url.includes("res.cloudinary.com") && !url.includes("fl_attachment") ? url.replace("/upload/", "/upload/fl_attachment/") : url;

/** Nombre base: "Espacio Marca_RefCliente" (omitiendo lo que falte). */
export const nombreBaseReporte = (reporte) => {
  const espacio = limpiar(reporte?.espacio?.nombre || reporte?.parada?.manual_titulo);
  const marca = limpiar(reporte?.peticion?.marca);
  const ref = limpiar(reporte?.linea?.referencia_cliente);
  const cab = [espacio, marca].filter(Boolean).join(" ");
  return [cab, ref].filter(Boolean).join("_") || limpiar(reporte?.peticion?.codigo) || "reporte";
};

/**
 * Descarga todas las fotos/vídeos del reporte a una carpeta elegida por el usuario
 * (File System Access API, Chrome/Edge). En navegadores sin soporte → ZIP.
 */
export default function DescargarFotosCarpeta({ reporte, fotos = [], className = "" }) {
  const [busy, setBusy] = useState(null);
  if (!fotos.length) return null;
  const base = nombreBaseReporte(reporte);
  const soportaCarpeta = typeof window !== "undefined" && "showDirectoryPicker" in window;

  const nombreDe = (f, i) => {
    const cat = f.categoria && f.categoria !== "libre" ? `_${limpiar(f.categoria)}` : "";
    return `${base}${cat}_${String(i + 1).padStart(2, "0")}.${extDe(f)}`;
  };

  const descargarBlobs = async (onFile) => {
    let ok = 0; const fallos = [];
    for (let i = 0; i < fotos.length; i++) {
      const f = fotos[i];
      setBusy(`${i + 1}/${fotos.length}`);
      try {
        const resp = await fetch(urlDescarga(f.url), { mode: "cors", credentials: "omit" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        await onFile(nombreDe(f, i), await resp.blob());
        ok++;
      } catch (e) {
        console.error("Fallo descarga", f.url, e);
        fallos.push(nombreDe(f, i));
      }
    }
    return { ok, fallos };
  };

  const aCarpeta = async () => {
    let dir;
    try {
      dir = await window.showDirectoryPicker({ mode: "readwrite", id: "reportes-dnf", startIn: "pictures" });
    } catch (e) {
      if (e?.name === "AbortError") return;
      throw e;
    }
    setBusy("…");
    const { ok, fallos } = await descargarBlobs(async (nombre, blob) => {
      const fh = await dir.getFileHandle(nombre, { create: true });
      const w = await fh.createWritable();
      await w.write(blob); await w.close();
    });
    if (fallos.length) toast.warning(`${ok} archivo(s) guardados en "${dir.name}" · ${fallos.length} fallaron`);
    else toast.success(`✅ ${ok} archivo(s) guardados en la carpeta "${dir.name}"`);
  };

  const aZip = async () => {
    setBusy("…");
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    const { ok, fallos } = await descargarBlobs(async (nombre, blob) => zip.file(nombre, blob));
    if (!ok) throw new Error("No se pudo descargar ningún archivo");
    const blob = await zip.generateAsync({ type: "blob" });
    const zipName = `${base}.zip`;
    if ("showSaveFilePicker" in window) {
      try {
        const fh = await window.showSaveFilePicker({ suggestedName: zipName, types: [{ description: "ZIP", accept: { "application/zip": [".zip"] } }] });
        const w = await fh.createWritable(); await w.write(blob); await w.close();
        toast.success(`✅ ZIP guardado con ${ok} archivo(s)`);
        return;
      } catch (e) { if (e?.name === "AbortError") return; }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = zipName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`✅ ZIP descargado con ${ok} archivo(s)${fallos.length ? ` · ${fallos.length} fallaron` : ""}`);
  };

  const run = async () => {
    try { soportaCarpeta ? await aCarpeta() : await aZip(); }
    catch (e) { toast.error(e?.message || "Error descargando archivos"); }
    finally { setBusy(null); }
  };

  return (
    <button type="button" onClick={run} disabled={!!busy} data-testid="descargar-fotos-carpeta-btn"
      title={`${soportaCarpeta ? "Elegir carpeta y guardar" : "Descargar ZIP con"} ${fotos.length} archivo(s) como "${base}_01.jpg"…`}
      className={`text-[10px] uppercase tracking-wider bg-sky-500/15 hover:bg-sky-500/25 text-sky-200 border border-sky-500/30 rounded px-2 py-0.5 inline-flex items-center gap-1 disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderDown className="w-3 h-3" />}
      {busy ? `Descargando ${busy}` : (soportaCarpeta ? "Descargar a carpeta" : "Descargar ZIP")}
    </button>
  );
}
